import React, { useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "src/components/ui/Card";
import { Button } from "src/components/ui/Button";
import { Edit, ExternalLink, BookOpen, Plus, Trash2 } from "lucide-react";
import { Modal } from "src/components/ui/Modal";
import { FormInput } from "src/components/ui/FormInput";
import { TutorExtendedProfileData, TutorSectionType } from "src/interfaces/TutorProfile";
import { useTutorProfileEditStore } from "src/store/tutorProfileEditStore";

interface TutorExpertiseSectionProps {
  profileData: TutorExtendedProfileData;
  editData: Partial<TutorExtendedProfileData>;
  activeSectionEdit: TutorSectionType | null;
  setActiveSectionEdit: (section: TutorSectionType | null) => void;
  updateEditData: (field: string, value: any) => void;
  updateProfile: (data: Partial<TutorExtendedProfileData>) => Promise<void>;
  isModified?: boolean;
}

const TutorExpertiseSection: React.FC<TutorExpertiseSectionProps> = ({
  profileData,
  editData,
  activeSectionEdit,
  setActiveSectionEdit,
  updateEditData,
  updateProfile,
  isModified = false
}) => {
  const {
    editingExpertise,
    setEditingExpertise,
    addExpertise,
    removeExpertise,
    updateExpertise
  } = useTutorProfileEditStore();

  // Sync expertise with updateEditData whenever editingExpertise changes
  useEffect(() => {
    if (activeSectionEdit === 'expertise') {
      updateEditData("expertise", editingExpertise);
    }
  }, [editingExpertise, activeSectionEdit, updateEditData]);
  
  // Helper function to check if a string is a valid URL
  const isValidUrl = (string: string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  return (
    <>
      {/* Expertise Section */}
      <Card className={isModified ? "ring-2 ring-blue-200 bg-blue-50/30" : ""}>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Expertise & Articles</CardTitle>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 p-0 rounded-full hover:bg-gray-100"
            onClick={() => {
              const currentExpertise = editData.expertise || profileData.expertise;
              setEditingExpertise(currentExpertise);
              setActiveSectionEdit('expertise');
            }}
            aria-label="Edit Expertise & Articles"
          >
            <Edit className="h-4 w-4 text-gray-500" />
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {profileData.expertise && profileData.expertise.length > 0 ? (
              profileData.expertise.map((item, index) => (
                <div key={index} className="flex items-start space-x-4 p-3 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <BookOpen className="h-5 w-5 text-blue-500" />
                  </div>
                  <div className="flex-1">
                    {isValidUrl(item) ? (
                      <a
                        href={item}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline flex items-center"
                      >
                        <span className="mr-1">{item}</span>
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    ) : (
                      <p className="text-sm text-gray-700">{item}</p>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500">No expertise articles or links provided</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Expertise Edit Modal */}
      <Modal
        isOpen={activeSectionEdit === 'expertise'}
        onClose={() => setActiveSectionEdit(null)}
        title="Edit Expertise & Articles"
      >
        <div className="space-y-4">
          <div className="text-sm text-gray-500 mb-4">
            Share links to your articles, blog posts, research papers, or describe your areas of expertise. URLs will be displayed as clickable links on your profile.
          </div>
          
          <div className="flex items-center justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addExpertise}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Item
            </Button>
          </div>

          {editingExpertise.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <BookOpen className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No expertise items added yet</p>
              <p className="text-sm">Click "Add Item" to share your expertise</p>
            </div>
          ) : (
            <div className="space-y-3">
              {editingExpertise.map((item, index) => (
                <div key={index}>
                  {/* Label above the container */}
                  <div className="mb-1 text-sm font-semibold text-gray-700">Item {index + 1}</div>
                  <div className="flex items-start space-x-3 p-3 border rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-1">
                      <BookOpen className="h-4 w-4 text-blue-500" />
                    </div>
                    <div className="flex-1">
                      <FormInput
                        label=""
                        id={`expertise-${index}`}
                        type="textarea"
                        value={item}
                        onChange={(e) => updateExpertise(index, e.target.value)}
                        placeholder="Enter article link (https://...) or describe your expertise area..."
                        helpText={isValidUrl(item) ? "✓ Valid URL detected" : "Enter a URL or description"}
                        rows={2}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeExpertise(index)}
                      className="text-red-600 hover:text-red-700 flex-shrink-0 mt-6"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setActiveSectionEdit(null)}>
              Cancel
            </Button>
            <Button onClick={() => {
              updateProfile(editData);
              setActiveSectionEdit(null);
            }}>
              Save
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default TutorExpertiseSection;